/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/compensationMasterData.ts
 * PURPOSE: Types + API base paths for the Job Family / Pay Grade master-data
 *          endpoints (BR-COMP-002 §8 R1/R2). These entities live under
 *          /api/v2/compensation, so they use `apiClientV2` (whose baseURL has
 *          no /api/v1 prefix) rather than the default `apiClient`.
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-12
 * COMPLIANCE: SDLC 6.3.1 + BR-PLAT-001-MASTER-DATA-CRUD-IMPORT-EXPORT-STANDARD
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * @business-context
 * - Domain: Vietnamese Compensation Master Data (Compensation/KPI V2.4)
 * - Impact: Revenue Critical — job-family multiplier is the SSOT of Q2 bonus (DD-0079)
 * - Stakeholders: CFO / HR Director (TENANT_ADMIN)
 *
 * @technical-architecture
 * - Layer: Frontend Service Layer (API client bindings)
 * - Pattern: Typed DTOs mirroring backend Pydantic response models
 * - Dependencies: frontend/src/services/api/client.ts (apiClientV2)
 *
 * @cross-references
 * - Backend API: backend/app/api/v2/endpoints/compensation_master_data.py
 * - Backend schemas: backend/app/schemas/compensation_master_data.py
 * - BR: docs/01-planning/01-requirements/08-compensation/BR-COMP-002-job-family-paygrade-management.md (§8.1)
 * - Schemas: frontend/src/pages/CompensationMasterData/
 */

import { apiClientV2 } from '@/services/api/client';

/** Base paths — mounted at /api/v2/compensation (see backend/app/api/v2/__init__.py). */
export const JOB_FAMILY_API_BASE = '/api/v2/compensation/job-families';
export const PAY_GRADE_API_BASE = '/api/v2/compensation/pay-grades';

/** Column set == BR-COMP-002 §8.1 (job_families table). */
export interface JobFamily {
  id: string;
  code: string;
  name: string;
  name_localized?: Record<string, string> | null;
  description?: string | null;
  grade_min: number;
  grade_max: number;
  /** DD-0079: DB column is the single source of truth for Q2 bonus calculation. */
  multiplier: number | string;
  assignment_rules?: Record<string, unknown> | null;
}

/** Column set == BR-COMP-002 §8.1 (pay_grades table). */
export interface PayGrade {
  id: string;
  level: number;
  name: string;
  name_localized?: Record<string, string> | null;
  tier?: string | null;
  min_salary: number | string;
  max_salary: number | string;
  step_amount: number | string;
  currency: string;
}

/** The client MasterDataCrudPage should use for these two entities. */
export const compensationMasterDataClient = apiClientV2;
